import { Box, IconButton, Stack, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

/**
 * Page title block: optional back arrow, title, subtitle, and a right-aligned
 * actions slot. Use once per page, immediately inside `<PageContainer>`.
 *
 * Props:
 *  - title:    string | ReactNode
 *  - subtitle: string | ReactNode (optional)
 *  - back:     boolean — show back arrow that calls navigate(-1)
 *  - onBack:   () => void — override default back behavior
 *  - actions:  ReactNode — right-side action buttons/menus
 */
export default function PageHeader({
  title,
  subtitle,
  back,
  onBack,
  actions,
  sx,
}) {
  const navigate = useNavigate();
  const handleBack = onBack ?? (() => navigate(-1));

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "stretch", sm: "flex-start" }}
      justifyContent="space-between"
      spacing={2}
      sx={{ mb: { xs: 2.5, sm: 3 }, ...sx }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
        {back && (
          <IconButton
            onClick={handleBack}
            size="small"
            aria-label="Go back"
            sx={{ mt: 0.25 }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ lineHeight: 1.2 }}
            noWrap
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
      {actions && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ flexShrink: 0 }}
        >
          {actions}
        </Stack>
      )}
    </Stack>
  );
}
